/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * One list (docs/SHOPPING_LIST_10X.md §4.2–4.7).
 *
 *   - The add bar is at the TOP and understands what you type: "2 lemons",
 *     "500g mince", "milk x2", "eggs!" (needed today), "bread - sourdough".
 *     It autocompletes from what this family buys, and a pasted list (or a
 *     comma list) becomes several items in one go.
 *   - Where to buy it: any item can name a store. The store chosen for
 *     something is filled in the next time anyone adds it, and the list can
 *     be narrowed to one store. Never required.
 *   - Usuals that are due, and recently bought, sit one tap away.
 *   - Items group by category in the family's order; tap one to change
 *     anything about it, including its category, store and list.
 *   - Every change has an Undo, every item says who added it, and it's live.
 */

import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { ShoppingApi } from "../../api/endpoints.ts";
import {
  ShoppingLiveApi, type LiveItem, type ParsedItem, type Suggestion,
} from "../../api/shoppingLive.ts";
import { useToday } from "../../lib/useToday.tsx";
import { Banner, ErrorBox, Loading, Modal } from "../../components/ui.tsx";
import { Icon, IconButton } from "../../components/Icon.tsx";
import { ActionMenu } from "../../components/ActionMenu.tsx";
import { RegeneratePanel, timeAgo } from "./ShoppingListsView.tsx";
import {
  NewStoreModal, PlanMergeModal, SaveTemplateModal, ShareModal, StoreSelect, TemplatesModal, UsualsStrip,
} from "./ShoppingManage.tsx";
import { LiveBadge, useShoppingLive, useUndoable, type NewOp } from "./live/useShoppingLive.tsx";
import {
  describeQuantity, grouped, homeListId, itemsOf, liveLists, memberName, sessionsOn, sharesOf,
  storeName, whoAdded, type Model,
} from "./live/model.ts";

type OpenSheet = "share" | "saveTemplate" | "templates" | "plan" | null;

export function ShoppingListView() {
  const { id: rawId = "" } = useParams();
  const { today } = useToday();
  const { model, status, error, unsent } = useShoppingLive();
  const { run, bar } = useUndoable(model);
  const [editing, setEditing] = useState<string | null>(null);
  const [showGot, setShowGot] = useState(false);
  const [open, setOpen] = useState<OpenSheet>(null);
  // undefined = all stores; null = only things with no store; id = that store
  const [storeFilter, setStoreFilter] = useState<string | null | undefined>(undefined);
  const qc = useQueryClient();

  if (!model) {
    if (status === "error") return <ErrorBox message={error ?? "Couldn't load the list."} />;
    return <Loading label="Loading your list…" />;
  }

  const id = rawId === "home" ? homeListId(model) ?? "" : rawId;
  const list = model.lists[id];
  if (!list) {
    return (
      <div className="space-y-3">
        <Link to="/shopping" className="text-sm text-ink-3 hover:text-ink">← Lists</Link>
        <Banner tone="warn">This list isn't here any more — someone may have deleted it.</Banner>
      </div>
    );
  }

  const all = itemsOf(model, id);
  const groups = grouped(model, id, { store: storeFilter });
  const got = all.filter((i) => i.status === "got");
  const editingItem = editing ? model.items[editing] : undefined;
  const shoppers = sessionsOn(model, id).filter((s) => s.member_sub !== model.me);
  const shares = sharesOf(model, id);
  const storesHere = model.stores.filter((s) => all.some((i) => i.store_id === s.id && i.status !== "got"));

  const tick = (i: LiveItem) => run(
    [{ op: "item.status", itemId: i.id, status: i.status === "got" ? "needed" : "got" }],
    i.status === "got" ? null : `Ticked ${i.display_name}`);

  return (
    <div className="space-y-4 pb-20">
      <div className="no-print flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <Link to="/shopping" className="text-sm text-ink-3 hover:text-ink">← Lists</Link>
          <h1 className="type-title flex items-center gap-2">
            <span aria-hidden>{list.icon ?? (list.kind === "home" ? "🏠" : "🛒")}</span>
            <span className="truncate">{list.title}</span>
          </h1>
          <LiveBadge status={status} unsent={unsent} />
        </div>
        <div className="flex items-center gap-2">
          <Link className="btn-primary" to={`/shopping/${id}/mode`}>
            <Icon name="cart" className="h-4 w-4" /> Shop
          </Link>
          <ActionMenu items={[
            { label: "Add the meal plan", icon: "calendar", onSelect: () => setOpen("plan") },
            { label: "Add from a template", icon: "copy", onSelect: () => setOpen("templates") },
            { label: "Save as a template", icon: "star", onSelect: () => setOpen("saveTemplate") },
            { label: "Share with someone outside the family", icon: "link", onSelect: () => setOpen("share") },
            { label: "Print", icon: "printer", onSelect: () => void ShoppingApi.exportPdf(id, today ?? "list") },
            ...(list.kind !== "home" ? [{
              label: "Archive this list", icon: "archive" as const, destructive: true,
              onSelect: () => run([{ op: "list.archive", listId: id }], `Archived "${list.title}"`),
            }] : []),
          ]} />
        </div>
      </div>

      {shoppers.length > 0 && (
        <Banner>
          {shoppers.map((s) => `${memberName(model, s.member_sub)}${s.store_id ? ` (at ${storeName(model, s.store_id)})` : ""}`).join(", ")}
          {shoppers.length === 1 ? " is" : " are"} shopping this list now — anything you add, they'll see.
        </Banner>
      )}
      {shares.length > 0 && (
        <p className="no-print text-xs text-ink-3">
          <Icon name="link" className="mr-1 inline h-3 w-3" />
          Shared with {shares.map((s) => s.label).join(", ")} via link.{" "}
          <button className="text-accent hover:underline" onClick={() => setOpen("share")}>Manage</button>
        </p>
      )}

      {list.is_stale && (
        <div className="no-print space-y-2">
          <Banner tone="warn">The meal plan changed after it was added to this list.</Banner>
          <RegeneratePanel listId={id} onDone={() => {
            void qc.invalidateQueries({ queryKey: ["shopping"] });
          }} />
        </div>
      )}

      <AddBar listId={id} model={model} empty={all.length === 0} storeFilter={storeFilter}
              onAdd={(ops, label) => run(ops, label)} />

      <UsualsStrip listId={id} model={model}
        onAdd={(op, label) => run([{ ...op, itemId: crypto.randomUUID(), listId: id }], label)} />

      {storesHere.length > 0 && (
        <div className="no-print flex flex-wrap items-center gap-1.5" role="group" aria-label="Show items for">
          <button className="chip-toggle" aria-pressed={storeFilter === undefined}
            onClick={() => setStoreFilter(undefined)}>All</button>
          {storesHere.map((s) => (
            <button key={s.id} className="chip-toggle" aria-pressed={storeFilter === s.id}
              onClick={() => setStoreFilter(s.id)}>{s.icon ? `${s.icon} ` : ""}{s.name}</button>
          ))}
          <button className="chip-toggle" aria-pressed={storeFilter === null}
            onClick={() => setStoreFilter(null)}>No store</button>
        </div>
      )}

      {groups.length === 0 && got.length === 0 && (
        <p className="py-6 text-center text-sm text-ink-3">
          Nothing on this list yet. Type above — "2 lemons", "milk x2", or paste a whole list.
        </p>
      )}

      <div className="space-y-4">
        {groups.map(({ category, items }) => (
          <section key={category.key}>
            <h2 className="label-mono mb-1.5 flex items-center gap-1.5">
              {category.icon && <span aria-hidden>{category.icon}</span>}
              {category.name}
              <span className="text-ink-4">· {items.length}</span>
            </h2>
            <div className="card divide-y divide-rule-soft">
              {items.map((i) => (
                <ItemRow key={i.id} item={i} model={model}
                  onTick={() => tick(i)}
                  onEdit={() => setEditing(i.id)}
                  onDelete={() => run([{ op: "item.delete", itemId: i.id }], `Removed ${i.display_name}`)} />
              ))}
            </div>
          </section>
        ))}
      </div>

      {got.length > 0 && (
        <section className="no-print">
          <div className="flex items-center justify-between">
            <button className="label-mono flex items-center gap-1 text-ink-3 hover:text-ink"
                    aria-expanded={showGot} onClick={() => setShowGot((v) => !v)}>
              <Icon name={showGot ? "chevronDown" : "chevronRight"} className="h-3.5 w-3.5" />
              In the trolley ({got.length})
            </button>
            <button className="text-xs text-accent hover:underline"
                    onClick={() => run([{ op: "list.clearGot", listId: id }],
                      `Cleared ${got.length} ticked item${got.length === 1 ? "" : "s"}`)}>
              Clear ticked
            </button>
          </div>
          {showGot && (
            <div className="card mt-1.5 divide-y divide-rule-soft">
              {got.map((i) => (
                <ItemRow key={i.id} item={i} model={model}
                  onTick={() => tick(i)}
                  onEdit={() => setEditing(i.id)}
                  onDelete={() => run([{ op: "item.delete", itemId: i.id }], `Removed ${i.display_name}`)} />
              ))}
            </div>
          )}
        </section>
      )}

      {editingItem && (
        <EditItemModal item={editingItem} model={model}
          onClose={() => setEditing(null)}
          run={run}
          onSave={(ops, label) => { run(ops, label); setEditing(null); }} />
      )}
      {open === "share" && <ShareModal model={model} run={run} listId={id} onClose={() => setOpen(null)} />}
      {open === "saveTemplate" && <SaveTemplateModal model={model} run={run} listId={id} onClose={() => setOpen(null)} />}
      {open === "templates" && <TemplatesModal model={model} run={run} listId={id} onClose={() => setOpen(null)} />}
      {open === "plan" && today && <PlanMergeModal model={model} today={today} initialListId={id} onClose={() => setOpen(null)} />}
      {bar}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ItemRow({ item: i, model, onTick, onEdit, onDelete }: {
  item: LiveItem; model: Model;
  onTick: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const got = i.status === "got";
  const qty = describeQuantity(i);
  const store = storeName(model, i.store_id);
  const details = [store && `at ${store}`, i.brand, i.notes, i.quantity_note].filter(Boolean).join(" · ");
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
      <button
        className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${got ? "text-accent" : "text-ink-3 hover:text-ink"}`}
        aria-pressed={got}
        aria-label={got ? `Untick ${i.display_name}` : `Tick ${i.display_name}`}
        onClick={onTick}
      >
        <Icon name={got ? "check" : "circle"} className="h-5 w-5" />
      </button>
      <button className="min-w-0 flex-1 py-1 text-left" onClick={onEdit}
              aria-label={`Edit ${i.display_name}`}>
        <span className={got ? "text-ink-3 line-through" : ""}>
          {i.display_name}
          {qty && <strong className="ml-1.5">{qty}</strong>}
        </span>
        {i.urgent && !got && <span className="chip ml-2 border-danger/40 text-danger">today</span>}
        {i.status === "unavailable" && <span className="chip ml-2 border-warn/40 text-warn">couldn't find</span>}
        <span className="block truncate text-xs text-ink-3">
          {details}{details ? " · " : ""}{whoAdded(model, i)} · {timeAgo(i.updated_at)}
        </span>
      </button>
      <IconButton name="x" tone="danger" label={`Remove ${i.display_name}`} onClick={onDelete} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The add bar
// ─────────────────────────────────────────────────────────────────────────────

const looksLikeAList = (t: string) => /[\n;]/.test(t) || (t.split(",").length > 2 && !/\d,\d/.test(t));

function AddBar({ listId, model, empty, storeFilter, onAdd }: {
  listId: string; model: Model; empty: boolean; storeFilter: string | null | undefined;
  onAdd: (ops: NewOp[], label: string | null) => void;
}) {
  const [addingStore, setAddingStore] = useState(false);
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");
  const [preview, setPreview] = useState<ParsedItem[] | null>(null);
  const [parsing, setParsing] = useState(false);
  const [active, setActive] = useState(-1);
  // "auto" = the store the family usually uses for each thing (or none)
  const [store, setStore] = useState<string | "auto">("auto");
  const input = useRef<HTMLTextAreaElement>(null);

  // Narrowed to one store? New things go to that store unless said otherwise.
  useEffect(() => { setStore(storeFilter ? storeFilter : "auto"); }, [storeFilter]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), 150);
    return () => clearTimeout(t);
  }, [text]);

  const suggest = useQuery({
    queryKey: ["shopping", "suggest", debounced],
    queryFn: () => ShoppingLiveApi.suggest(debounced),
    enabled: debounced.length >= 2 && !looksLikeAList(debounced),
    staleTime: 60_000,
  });
  const recent = useQuery({
    queryKey: ["shopping", "recent", listId],
    queryFn: () => ShoppingLiveApi.recent(listId),
    staleTime: 30_000,
  });

  const needed = useMemo(() => new Set(itemsOf(model, listId)
    .filter((i) => i.status !== "got").map((i) => i.display_name.toLowerCase())), [model, listId]);
  const suggestions = (suggest.data?.suggestions ?? [])
    .filter((s) => !needed.has(s.name.toLowerCase())).slice(0, 6);
  const recentItems = (recent.data?.items ?? [])
    .filter((r) => !needed.has(r.name.toLowerCase())).slice(0, 10);

  const storeField = (remembered?: string | null): Partial<NewOp> =>
    store !== "auto" ? { storeId: store } : remembered ? { storeId: remembered } : {};

  const addOne = (op: NewOp, label: string) => {
    onAdd([{ itemId: crypto.randomUUID(), listId, ...op, op: "item.add" }], label);
  };

  const pick = (s: Suggestion) => {
    addOne({ op: "item.add", name: s.name, aisle: s.aisle ?? undefined, ...storeField(s.storeId) }, `Added ${s.name}`);
    setText(""); setActive(-1);
    input.current?.focus();
  };

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    if (active >= 0 && suggestions[active]) { pick(suggestions[active]!); return; }
    if (looksLikeAList(t)) {
      setParsing(true);
      try {
        const r = await ShoppingLiveApi.parse(t);
        setPreview(r.items);
      } finally {
        setParsing(false);
      }
      return;
    }
    addOne({ op: "item.add", text: t, ...storeField() }, `Added ${t}`);
    setText(""); setActive(-1);
  };

  const addPreview = () => {
    if (!preview?.length) return;
    onAdd(preview.map((p) => ({
      op: "item.add", itemId: crypto.randomUUID(), listId, name: p.name,
      quantity: p.quantity ?? undefined, unit: p.unit ?? undefined,
      notes: p.notes ?? undefined, urgent: p.urgent, aisle: p.aisle,
      ...storeField(p.storeId),
    })), `Added ${preview.length} items`);
    setPreview(null); setText("");
    input.current?.focus();
  };

  return (
    <div className="no-print space-y-2">
      <div className="relative">
        <div className="flex items-start gap-2">
          <textarea
            ref={input}
            className="input min-h-[44px] flex-1 resize-none"
            rows={text.includes("\n") ? Math.min(6, text.split("\n").length) : 1}
            value={text}
            autoFocus={empty}
            placeholder={'Add… "2 lemons", "milk x2" — or paste a list'}
            aria-label="Add to the list"
            aria-autocomplete="list"
            onChange={(e) => { setText(e.target.value); setActive(-1); setPreview(null); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void submit(); }
              else if (e.key === "ArrowDown" && suggestions.length) {
                e.preventDefault(); setActive((a) => Math.min(suggestions.length - 1, a + 1));
              } else if (e.key === "ArrowUp" && suggestions.length) {
                e.preventDefault(); setActive((a) => Math.max(-1, a - 1));
              } else if (e.key === "Escape") { setText(""); setPreview(null); }
            }}
          />
          <button className="btn-primary h-11 shrink-0" onClick={() => void submit()}
                  disabled={!text.trim() || parsing} aria-label="Add">
            <Icon name="plus" className="h-4 w-4" />
          </button>
        </div>

        {suggestions.length > 0 && text.trim().length >= 2 && !preview && (
          <ul className="card absolute inset-x-0 top-full z-20 mt-1 overflow-hidden py-1" role="listbox">
            {suggestions.map((s, i) => (
              <li key={`${s.source}:${s.name}`} role="option" aria-selected={i === active}>
                <button className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ${
                          i === active ? "bg-paper-3" : "hover:bg-paper-3"}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(s)}>
                  <span>{s.name}</span>
                  <span className="text-xs text-ink-3">
                    {model.categories.find((c) => c.key === s.aisle)?.name ?? ""}
                    {s.storeId && storeName(model, s.storeId) ? ` · ${storeName(model, s.storeId)}` : ""}
                    {s.source === "history" ? " · you buy this" : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-ink-3">
        <Icon name="cart" className="h-3.5 w-3.5" />
        <span>From</span>
        <select className="rounded-md border border-rule-soft bg-paper px-1.5 py-1 text-xs text-ink"
          value={store} aria-label="From which store"
          title="Where to buy it — 'the usual store' fills in whatever the family chose for it last time"
          onChange={(e) => {
            if (e.target.value === "__new") { setAddingStore(true); return; }
            setStore(e.target.value as string);
          }}>
          <option value="auto">the usual store</option>
          <option value="">no particular store</option>
          {model.stores.map((s) => <option key={s.id} value={s.id}>{s.icon ? `${s.icon} ` : ""}{s.name}</option>)}
          <option value="__new">+ Add a store…</option>
        </select>
      </div>

      {preview && (
        <div className="card space-y-2 p-3 text-sm">
          <p className="font-medium">{preview.length} item{preview.length === 1 ? "" : "s"} from that:</p>
          <div className="flex flex-wrap gap-1.5">
            {preview.map((p, i) => (
              <span key={i} className="chip">
                {p.name}{p.quantity !== null ? ` ${describeQuantity(p)}` : ""}
                {p.urgent ? " · today" : ""}
                {store === "auto" && p.storeId && storeName(model, p.storeId) ? ` · ${storeName(model, p.storeId)}` : ""}
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" onClick={addPreview}>Add all</button>
            <button className="btn-ghost" onClick={() => setPreview(null)}>Edit</button>
          </div>
        </div>
      )}

      {recentItems.length > 0 && !text && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-ink-3">Bought before:</span>
          {recentItems.map((r) => (
            <button key={r.name} className="chip-toggle"
                    onClick={() => addOne({
                      op: "item.add", name: r.name, aisle: r.aisle ?? undefined,
                      quantity: r.quantity ?? undefined, unit: r.unit ?? undefined,
                      ...storeField(r.storeId),
                    }, `Added ${r.name}`)}>
              <Icon name="plus" className="h-3 w-3" /> {r.name}
            </button>
          ))}
        </div>
      )}
      {addingStore && (
        <NewStoreModal run={onAdd} onClose={() => setAddingStore(false)}
          onCreated={(id) => setStore(id)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Editing
// ─────────────────────────────────────────────────────────────────────────────

function EditItemModal({ item, model, onClose, onSave, run }: {
  item: LiveItem; model: Model; onClose: () => void;
  onSave: (ops: NewOp[], label: string | null) => void;
  run: (ops: NewOp[], label: string | null) => void;
}) {
  const [addingStore, setAddingStore] = useState(false);
  const [name, setName] = useState(item.display_name);
  const [quantity, setQuantity] = useState(item.quantity?.toString() ?? "");
  const [unit, setUnit] = useState(item.unit ?? "");
  const [aisle, setAisle] = useState(item.aisle ?? "other");
  const [notes, setNotes] = useState(item.notes ?? "");
  const [brand, setBrand] = useState(item.brand ?? "");
  const [urgent, setUrgent] = useState(item.urgent);
  const [listId, setListId] = useState(item.list_id);
  const [storeId, setStoreId] = useState<string | null>(item.store_id ?? null);
  const lists = liveLists(model);

  // What the family chose for this before — the recommendation, if it differs.
  const recall = useQuery({
    queryKey: ["shopping", "recall", name.trim().toLowerCase()],
    queryFn: () => ShoppingLiveApi.recall(name.trim()),
    enabled: name.trim().length > 1,
    staleTime: 30_000,
  });
  const recommended = recall.data?.storeId ?? null;

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const fields: Record<string, unknown> = {};
    const q = quantity.trim() ? Number(quantity.replace(",", ".")) : null;
    if (name.trim() && name.trim() !== item.display_name) fields.name = name.trim();
    if ((Number.isFinite(q) ? q : null) !== item.quantity) fields.quantity = Number.isFinite(q) ? q : null;
    if ((unit.trim() || null) !== item.unit) fields.unit = unit.trim() || null;
    if (aisle !== (item.aisle ?? "other")) fields.category = aisle;
    if ((notes.trim() || null) !== item.notes) fields.notes = notes.trim() || null;
    if ((brand.trim() || null) !== item.brand) fields.brand = brand.trim() || null;
    if (urgent !== item.urgent) fields.urgent = urgent;
    if (storeId !== (item.store_id ?? null)) fields.storeId = storeId;

    const ops: NewOp[] = [];
    if (Object.keys(fields).length) ops.push({ op: "item.update", itemId: item.id, fields });
    let label: string | null = ops.length ? `Updated ${item.display_name}` : null;
    if (listId !== item.list_id) {
      ops.push({ op: "item.move", itemId: item.id, toListId: listId, newItemId: crypto.randomUUID() });
      label = `Moved to ${model.lists[listId]?.title ?? "another list"}`;
    }
    if (ops.length) onSave(ops, label); else onClose();
  };

  return (
    <Modal title="Edit item" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <label className="block">
          <span className="text-sm">Name</span>
          <input className="input mt-1" value={name} autoFocus maxLength={200}
                 onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-sm">How many / much</span>
            <input className="input mt-1" inputMode="decimal" value={quantity} placeholder="—"
                   onChange={(e) => setQuantity(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-sm">Unit</span>
            <input className="input mt-1" value={unit} placeholder="g, kg, l, pack…"
                   onChange={(e) => setUnit(e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="text-sm">Where to buy it <span className="text-ink-3">(optional)</span></span>
          <StoreSelect className="mt-1" model={model} value={storeId} onChange={setStoreId}
            recommended={recommended} onAddStore={() => setAddingStore(true)} />
          {recommended && recommended !== storeId && (
            <button type="button" className="mt-1 text-xs text-accent hover:underline"
              onClick={() => setStoreId(recommended)}>
              Usually from {storeName(model, recommended)} — use that
            </button>
          )}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-sm">Category</span>
            <select className="input mt-1" value={aisle} onChange={(e) => setAisle(e.target.value)}>
              {model.categories.filter((c) => !c.hidden || c.key === aisle).map((c) => (
                <option key={c.key} value={c.key}>{c.icon ? `${c.icon} ` : ""}{c.name}</option>
              ))}
              {!model.categories.some((c) => c.key === aisle) && <option value={aisle}>{aisle}</option>}
            </select>
          </label>
          <label className="block">
            <span className="text-sm">List</span>
            <select className="input mt-1" value={listId} onChange={(e) => setListId(e.target.value)}>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.icon ? `${l.icon} ` : ""}{l.title}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-sm">Note</span>
            <input className="input mt-1" value={notes} placeholder="ripe ones"
                   onChange={(e) => setNotes(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-sm">Brand</span>
            <input className="input mt-1" value={brand} placeholder="any"
                   onChange={(e) => setBrand(e.target.value)} />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
          Needed today
        </label>
        <p className="text-xs text-ink-3">Added by {whoAdded(model, item)}.</p>
        <div className="flex gap-2">
          <button type="button" className="btn-danger"
                  onClick={() => onSave([{ op: "item.delete", itemId: item.id }], `Removed ${item.display_name}`)}>
            <Icon name="trash" className="h-4 w-4" /> Remove
          </button>
          <span className="flex-1" />
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary">Save</button>
        </div>
      </form>
      {addingStore && (
        <NewStoreModal run={run} onClose={() => setAddingStore(false)}
          onCreated={(id) => setStoreId(id)} />
      )}
    </Modal>
  );
}
